// apps/prompts/src/lib/api-prompts.ts
import type { IComment, INotificationItem, IPromptUseCase } from '@neuroforge/shared'
import { api, useAuthStore, unwrapResponse as unwrap } from '@neuroforge/shared/client'

// ─── Types réponse API ───

export interface PromptListItem {
  id: string
  title: string
  slug: string
  description: string
  category: string
  aiModel: string
  tags: string[]
  useCaseCount: number
  thumbnailUrl: string | null
  copyCount: number
  viewCount: number
  favoriteCount: number
  order: number
  createdAt: string
  // Optionnel : présent dans la liste des favoris pour router un prompt privé vers l'édition.
  isPublished?: boolean
}

export interface PromptDetail {
  id: string
  title: string
  slug: string
  description: string
  content: string
  category: string
  aiModel: string
  tags: string[]
  variables: PromptVariable[]
  useCases: IPromptUseCase[]
  useCaseCount: number
  expectedOutput: string | null
  thumbnailUrl: string | null
  status: string
  isPublished: boolean
  copyCount: number
  viewCount: number
  favoriteCount: number
  order: number
  createdAt: string
  updatedAt: string
  // Anti-scraping : true quand le viewer n'est pas connecté → content est un
  // teaser tronqué côté serveur (PROMPTS-CONTENT-GATE-01).
  isContentTruncated?: boolean
  contentTotalLength?: number
}

export interface PromptVariable {
  name: string
  label: string
  type: 'TEXT' | 'NUMBER' | 'SELECT' | 'TEXTAREA'
  placeholder?: string
  options?: string[]
  required: boolean
  defaultValue?: string
}

export interface PromptListResponse {
  data: PromptListItem[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

export interface FavoritePromptItem extends PromptListItem {
  favoritedAt: string
}

// ─── API calls (toutes les réponses sont unwrappées) ───

export async function fetchPrompts(params: {
  category?: string
  aiModel?: string
  tag?: string
  page?: number
  limit?: number
  sort?: 'recent' | 'popular' | 'order'
}): Promise<PromptListResponse> {
  const res = await api.get<PromptListResponse>('/prompts', { params })
  return unwrap<PromptListResponse>(res)
}

// L'API accepte indifféremment l'id (cuid) ou le slug — l'id est utilisé par
// les URLs canoniques /prompts/:id/:slug, le slug reste supporté (liens legacy).
export async function fetchPromptBySlug(idOrSlug: string): Promise<PromptDetail> {
  const res = await api.get<PromptDetail>(`/prompts/${idOrSlug}`)
  return unwrap<PromptDetail>(res)
}

export async function trackPromptCopy(
  promptId: string,
): Promise<{ copied: boolean; newCount: number }> {
  const res = await api.post<{ copied: boolean; newCount: number }>(`/prompts/${promptId}/copy`)
  return unwrap<{ copied: boolean; newCount: number }>(res)
}

export async function toggleFavoriteApi(
  promptId: string,
): Promise<{ favorited: boolean; newCount: number }> {
  const res = await api.post<{ favorited: boolean; newCount: number }>(
    `/prompts/${promptId}/favorite`,
  )
  return unwrap<{ favorited: boolean; newCount: number }>(res)
}

export async function fetchFavorites(
  page = 1,
  limit = 50,
): Promise<{
  data: FavoritePromptItem[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}> {
  const res = await api.get('/prompts/favorites', { params: { page, limit } })
  return unwrap<{
    data: FavoritePromptItem[]
    meta: { total: number; page: number; limit: number; totalPages: number }
  }>(res)
}

// ─── Batch K: Related + History + View tracking + Suggest + Stats ───

export async function fetchRelatedPrompts(
  promptId: string,
  limit = 6,
): Promise<Array<PromptListItem & { relationType: string; relevanceScore: number }>> {
  const res = await api.get(`/prompts/${promptId}/related`, { params: { limit } })
  return unwrap(res)
}

export async function trackPromptView(
  promptId: string,
): Promise<{ tracked: boolean; debounced: boolean }> {
  const res = await api.post(`/prompts/${promptId}/view`)
  return unwrap(res)
}

export async function fetchPromptHistory(
  page = 1,
  limit = 20,
): Promise<{
  data: Array<PromptListItem & { viewedAt: string }>
  meta: { total: number; page: number; limit: number; totalPages: number }
}> {
  const res = await api.get('/prompts/history', { params: { page, limit } })
  return unwrap(res)
}

export async function fetchSearchSuggestions(
  query: string,
  limit = 6,
): Promise<{
  suggestions: Array<{ id: string; title: string; slug: string; category: string; aiModel: string }>
  query: string
  processingTimeMs?: number
}> {
  const res = await api.get('/search/suggest', { params: { q: query, limit } })
  return unwrap(res)
}

export async function fetchUserStats(): Promise<{
  totalViews: number
  totalCopies: number
  totalFavorites: number
}> {
  const res = await api.get('/prompts/me/stats')
  return unwrap(res)
}

export async function fetchPopularTags(limit = 20): Promise<Array<{ tag: string; count: number }>> {
  const res = await api.get<Array<{ tag: string; count: number }>>('/prompts/tags', {
    params: { limit },
  })
  return unwrap<Array<{ tag: string; count: number }>>(res)
}

// ─── Batch L: Collections + Share + Export ───

export interface CollectionListItem {
  id: string
  name: string
  slug: string
  description: string | null
  coverImage: string | null
  isPublic: boolean
  shareToken: string | null
  itemCount: number
  previewItems: Array<{ promptId: string; title: string; slug: string; category: string }>
  createdAt: string
  updatedAt: string
}

export interface CollectionDetail {
  id: string
  name: string
  slug: string
  description: string | null
  coverImage: string | null
  isPublic: boolean
  shareToken: string | null
  owner: { id: string; displayName: string; avatarUrl: string | null }
  itemCount: number
  items: Array<{
    id: string
    promptId: string
    note: string | null
    order: number
    addedAt: string
    prompt: {
      id: string
      title: string
      slug: string
      category: string
      description: string
      aiModel: string
      tags: string[]
      isPublished: boolean
      copyCount: number
      viewCount: number
      favoriteCount: number
    }
  }>
  createdAt: string
  updatedAt: string
}

export interface SharedCollectionDetail {
  id: string
  name: string
  slug: string
  description: string | null
  coverImage: string | null
  owner: { id: string; displayName: string; avatarUrl: string | null }
  itemCount: number
  items: Array<{
    id: string
    promptId: string
    note: string | null
    order: number
    prompt: {
      id: string
      title: string
      slug: string
      category: string
      description: string
      aiModel: string
      tags: string[]
      isPublished: boolean
      copyCount: number
      viewCount: number
    }
  }>
  shared: true
}

export async function fetchCollections(): Promise<CollectionListItem[]> {
  const res = await api.get('/collections')
  return unwrap(res)
}

export async function fetchCollectionBySlug(slug: string): Promise<CollectionDetail> {
  const res = await api.get(`/collections/${slug}`)
  return unwrap(res)
}

export async function fetchSharedCollection(token: string): Promise<SharedCollectionDetail> {
  const res = await api.get<SharedCollectionDetail>(`/collections/shared/${token}`)
  return unwrap<SharedCollectionDetail>(res)
}

export async function createCollection(data: {
  name: string
  description?: string
  coverImage?: string
  isPublic?: boolean
}) {
  const res = await api.post('/collections', data)
  return unwrap(res)
}

export async function updateCollection(
  id: string,
  data: { name?: string; description?: string; coverImage?: string; isPublic?: boolean },
) {
  const res = await api.patch(`/collections/${id}`, data)
  return unwrap(res)
}

export async function deleteCollection(id: string): Promise<void> {
  await api.delete(`/collections/${id}`)
}

export async function addCollectionItem(collectionId: string, promptId: string, note?: string) {
  const res = await api.post(`/collections/${collectionId}/items`, { promptId, note })
  return unwrap(res)
}

export async function removeCollectionItem(collectionId: string, promptId: string): Promise<void> {
  await api.delete(`/collections/${collectionId}/items/${promptId}`)
}

export async function reorderCollectionItems(
  collectionId: string,
  items: Array<{ itemId: string }>,
) {
  const res = await api.patch(`/collections/${collectionId}/items/reorder`, { items })
  return unwrap(res)
}

export async function enableCollectionSharing(
  collectionId: string,
): Promise<{ shareToken: string; shareUrl: string }> {
  const res = await api.post(`/collections/${collectionId}/share`)
  return unwrap(res)
}

export async function revokeCollectionSharing(collectionId: string): Promise<void> {
  await api.delete(`/collections/${collectionId}/share`)
}

// ─── Batch M: Ratings + Comments + Notifications ───

export async function ratePrompt(promptId: string, score: number, review?: string) {
  const res = await api.post(`/prompts/${promptId}/rate`, { score, review })
  return unwrap(res)
}

export async function fetchRatings(promptId: string, page = 1, limit = 20) {
  const res = await api.get(`/prompts/${promptId}/ratings`, { params: { page, limit } })
  return unwrap(res)
}

export async function fetchRatingSummary(promptId: string): Promise<{
  average: number
  total: number
  distribution: Record<number, number>
}> {
  const res = await api.get(`/prompts/${promptId}/ratings/summary`)
  return unwrap(res)
}

export async function fetchMyRating(
  promptId: string,
): Promise<{ rating: { id: string; score: number; review: string | null } | null }> {
  const res = await api.get(`/prompts/${promptId}/ratings/mine`)
  return unwrap(res)
}

export async function deleteRating(promptId: string): Promise<void> {
  await api.delete(`/prompts/${promptId}/ratings`)
}

export async function fetchComments(
  promptId: string,
  page = 1,
  limit = 20,
): Promise<{
  data: IComment[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}> {
  const res = await api.get(`/prompts/${promptId}/comments`, { params: { page, limit } })
  return unwrap(res)
}

export async function createComment(promptId: string, content: string, parentId?: string) {
  const res = await api.post(`/prompts/${promptId}/comments`, { content, parentId })
  return unwrap(res)
}

export async function deleteComment(commentId: string): Promise<void> {
  await api.delete(`/prompts/comments/${commentId}`)
}

export async function updateComment(commentId: string, content: string): Promise<IComment> {
  const res = await api.patch<IComment>(`/prompts/comments/${commentId}`, { content })
  return unwrap<IComment>(res)
}

export type NotificationStatusFilter = 'UNREAD' | 'READ' | 'ARCHIVED'

export async function fetchNotifications(
  page = 1,
  limit = 20,
  status?: NotificationStatusFilter,
): Promise<{
  data: INotificationItem[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}> {
  const res = await api.get('/notifications', { params: { page, limit, status } })
  return unwrap(res)
}

export async function fetchUnreadNotificationCount(): Promise<{ unreadCount: number }> {
  const res = await api.get<{ unreadCount: number }>('/notifications/unread-count')
  return unwrap<{ unreadCount: number }>(res)
}

export async function markNotificationRead(notificationId: string) {
  const res = await api.patch(`/notifications/${notificationId}/read`)
  return unwrap(res)
}

export async function markNotificationUnread(notificationId: string) {
  const res = await api.patch(`/notifications/${notificationId}/unread`)
  return unwrap(res)
}

export async function archiveNotification(notificationId: string) {
  const res = await api.patch(`/notifications/${notificationId}/archive`)
  return unwrap(res)
}

export async function markAllNotificationsRead(): Promise<void> {
  await api.patch('/notifications/read-all')
}

// ─── Patch V: Mes Prompts (forks + custom + demande publication) ───

export interface MyPromptItem {
  id: string
  title: string
  slug: string
  description: string
  category: string
  aiModel: string
  tags: string[]
  useCaseCount: number
  status: string // PublishStatus: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  isPublished: boolean
  parentId: string | null
  parent?: { id: string; title: string; slug: string } | null
  copyCount: number
  viewCount: number
  submissionStatus: string | null // PromptSubmission.status: 'PENDING' | 'APPROVED' | 'REJECTED' | null
  submissionId: string | null
  createdAt: string
  updatedAt: string
}

export async function fetchMyPrompts(
  params: {
    page?: number
    limit?: number
  } = {},
): Promise<{
  data: MyPromptItem[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}> {
  const res = await api.get('/prompts/mine', { params })
  return unwrap(res)
}

// Payload commun à la création d'un brouillon privé et à la proposition de mise à jour.
export interface MyPromptInput {
  title: string
  description: string
  content: string
  category: string
  aiModel: string
  tags: string[]
  variables: PromptVariable[]
  useCases: unknown[]
  expectedOutput?: string | null
}

// Crée un brouillon privé (DRAFT) appartenant à l'utilisateur.
export async function createMyPrompt(data: MyPromptInput): Promise<{ id: string; slug: string }> {
  const res = await api.post('/prompts/mine', data)
  return unwrap(res)
}

// Propose une mise à jour d'un prompt déjà publié (repasse en validation super admin).
export async function proposeMyPromptUpdate(
  promptId: string,
  data: Partial<MyPromptInput>,
): Promise<{ submissionId: string; status: string }> {
  const res = await api.post(`/prompts/mine/${promptId}/propose-update`, data)
  return unwrap(res)
}

export type PublicationMode = 'NEW_PROMPT' | 'IMPROVEMENT'

export async function requestPublication(
  promptId: string,
  payload: {
    publicationMode: PublicationMode
    targetPromptId?: string | null
  },
): Promise<{ submissionId: string; status: string }> {
  // Patch — préfixe "mine/" pour matcher le nouveau routing backend.
  const res = await api.post(`/prompts/mine/${promptId}/request-publication`, payload)
  return unwrap(res)
}

export interface MyPromptDetail {
  id: string
  title: string
  slug: string
  description: string
  content: string
  thumbnailUrl?: string | null
  category: string
  aiModel: string
  tags: string[]
  // Patch — champs administrables réintroduits dans le formulaire user.
  variables: PromptVariable[]
  useCases: unknown[] // string[] (titres) ou IPromptUseCase[]
  useCaseCount: number
  expectedOutput: string | null
  status: string
  isPublished: boolean
  parentId: string | null
  parent: { id: string; title: string; slug: string } | null
  copyCount: number
  viewCount: number
  submissionStatus: string | null
  submissionId: string | null
  createdAt: string
  updatedAt: string
}

export async function fetchMyPromptById(promptId: string): Promise<MyPromptDetail> {
  const res = await api.get(`/prompts/mine/${promptId}`)
  return unwrap<MyPromptDetail>(res)
}

export async function updateMyPrompt(
  promptId: string,
  data: {
    title?: string
    description?: string
    content?: string
    category?: string
    aiModel?: string
    tags?: string[]
    // Patch — nouveaux champs administrables (variables / cas d’usage / sortie attendue).
    // useCases : unknown[] pour accepter IPromptUseCase[] riche (validé par parsePromptUseCases backend).
    variables?: PromptVariable[]
    useCases?: unknown[]
    expectedOutput?: string | null
  },
): Promise<MyPromptItem> {
  // Patch — préfixe "mine/" : ne shadow plus le PATCH admin de PromptsController.
  const res = await api.patch(`/prompts/mine/${promptId}`, data)
  return unwrap(res)
}

export async function deleteMyPrompt(promptId: string): Promise<void> {
  // Patch — préfixe "mine/" : ne shadow plus le DELETE admin de PromptsController.
  await api.delete(`/prompts/mine/${promptId}`)
}

// ─── Export (Blob) — utilise fetch natif car ApiClient ne supporte pas les Blob ───
const EXPORT_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:6300/api/v1'

/** Helper : récupère le token JWT depuis le store Zustand (synchrone, pas d'import dynamique) */
function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function exportPrompt(id: string, format: string): Promise<Blob> {
  const res = await fetch(
    `${EXPORT_API_BASE}/export/prompt/${id}?format=${encodeURIComponent(format)}`,
    {
      headers: getAuthHeaders(),
    },
  )
  if (!res.ok) throw new Error(`Export failed: ${res.status}`)
  return res.blob()
}
